import React from 'react'
import { m } from 'framer-motion'
import { VotedKeywordsContent } from '../../../types/content.types'
import { useLanguage } from '../../../context/LanguageContext'
import { useTheme } from '../../../context/ThemeContext'

interface VotedKeywordsProps {
    content: VotedKeywordsContent
}

const TOP_N = 6

// 네이버 투표 키워드 code → 영어 라벨 (영어 모드용). 없으면 원문(한글) 사용.
const EN_LABELS: Record<string, string> = {
    food_good: 'Delicious food',
    meat_good: 'Great meat quality',
    kind: 'Friendly service',
    special_menu: 'Unique menu',
    large: 'Generous portions',
    fresh: 'Fresh ingredients',
    price_cheap: 'Great value',
    together: 'Good for groups',
    store_clean: 'Clean space',
    interior_cool: 'Stylish interior',
    parking: 'Easy parking',
    mood_good: 'Nice atmosphere',
}

const VotedKeywords: React.FC<VotedKeywordsProps> = ({ content }) => {
    const { language } = useLanguage()
    const { theme } = useTheme()

    const items = [...(content.items || [])]
        .sort((a, b) => b.count - a.count)
        .slice(0, TOP_N)
    if (items.length === 0) return null

    const label = (item: { code?: string; name: string }) =>
        language === 'en' && item.code && EN_LABELS[item.code] ? EN_LABELS[item.code] : item.name

    const max = items[0].count || 1
    const meta =
        language === 'ko'
            ? `${content.reviewCount.toLocaleString()}회 · ${content.userCount.toLocaleString()}명 참여`
            : `${content.reviewCount.toLocaleString()} mentions · ${content.userCount.toLocaleString()} guests`

    return (
        <section id="voted" className={`restaurant-voted theme-${theme.mode}`}>
            <div className="restaurant-voted-container">
                <m.div
                    className="restaurant-voted-header"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <span className="restaurant-voted-label">{content.label}</span>
                    <div className="restaurant-voted-divider" />
                    <h2 className="restaurant-voted-title">{content.title}</h2>
                    <p className="restaurant-voted-meta">{meta}</p>
                </m.div>

                <div className="restaurant-voted-list">
                    {items.map((item, i) => (
                        <m.div
                            key={item.name}
                            className={`restaurant-voted-row ${i === 0 ? 'is-top' : ''}`}
                            initial={{ opacity: 0, x: -16 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                        >
                            <span className="restaurant-voted-rank">{String(i + 1).padStart(2, '0')}</span>
                            <span className="restaurant-voted-name">{label(item)}</span>
                            <div className="restaurant-voted-track">
                                <m.div
                                    className="restaurant-voted-bar"
                                    initial={{ width: 0 }}
                                    whileInView={{ width: `${Math.max(6, (item.count / max) * 100)}%` }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.9, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                                />
                            </div>
                            <span className="restaurant-voted-count">{item.count.toLocaleString()}</span>
                        </m.div>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default React.memo(VotedKeywords)
